import { useCallback, useEffect, useState } from 'react';
import { Folder, CaretRight, CaretDown } from '@phosphor-icons/react';
import { nasApi } from './nasApi';

// NAS folder tree — read-only, source-derived, lazy-loaded. Independent of
// FolderTreePanel.js. No create/rename/move/delete/pin/access-badge controls.
// Implemented as a flat, iterative list (depth + expanded set) so there is no
// self-referential component.
export default function NasFolderTree({ rootId, selectedId, onSelect }) {
  // rows: [{ node_id, name, depth, parent_id }]
  const [rows, setRows] = useState([]);
  const [expanded, setExpanded] = useState({}); // node_id -> bool
  const [loading, setLoading] = useState({});   // node_id -> bool

  useEffect(() => {
    setExpanded({}); setRows([]);
    if (!rootId) return;
    nasApi.tree(rootId).then((r) => {
      setRows((r.directories || []).map((d) => ({
        node_id: d.node_id, name: d.name, depth: 0, parent_id: null,
      })));
    }).catch(() => setRows([]));
  }, [rootId]);

  const collapse = useCallback((nodeId) => {
    // remove all descendants
    setRows((prev) => {
      const toRemove = new Set();
      const collect = (pid) => prev.forEach((r) => {
        if (r.parent_id === pid) { toRemove.add(r.node_id); collect(r.node_id); }
      });
      collect(nodeId);
      return prev.filter((r) => !toRemove.has(r.node_id));
    });
  }, []);

  const toggle = useCallback(async (row) => {
    const isOpen = !!expanded[row.node_id];
    if (isOpen) {
      setExpanded((e) => ({ ...e, [row.node_id]: false }));
      collapse(row.node_id);
      return;
    }
    setExpanded((e) => ({ ...e, [row.node_id]: true }));
    setLoading((l) => ({ ...l, [row.node_id]: true }));
    try {
      const r = await nasApi.tree(rootId, row.node_id);
      const kids = (r.directories || []).map((d) => ({
        node_id: d.node_id, name: d.name, depth: row.depth + 1, parent_id: row.node_id,
      }));
      setRows((prev) => {
        const idx = prev.findIndex((x) => x.node_id === row.node_id);
        if (idx < 0) return prev;
        return [...prev.slice(0, idx + 1), ...kids, ...prev.slice(idx + 1)];
      });
    } catch { /* ignore */ } finally {
      setLoading((l) => ({ ...l, [row.node_id]: false }));
    }
  }, [expanded, rootId, collapse]);

  return (
    <aside className="w-full lg:w-64 shrink-0 rounded-lg border border-zinc-200 bg-zinc-50/60 p-2" data-testid="nas-folder-tree">
      <button type="button" onClick={() => onSelect(null)} data-testid="nas-tree-root"
        className={`w-full flex items-center gap-1.5 px-2 min-h-[32px] rounded-md text-sm text-left mb-1 ${
          !selectedId ? 'bg-teal-50 text-teal-900 font-semibold' : 'text-zinc-700 hover:bg-zinc-100'}`}>
        <Folder size={14} weight="duotone" className="text-teal-600" /> 承認範囲
      </button>
      {rows.length === 0 && <p className="text-xs text-zinc-400 px-2 py-2">サブフォルダーはありません</p>}
      {rows.map((row) => (
        <div key={row.node_id} className="flex items-center" style={{ paddingLeft: row.depth * 12 }}>
          <button type="button" onClick={() => toggle(row)}
            aria-label={expanded[row.node_id] ? '折りたたむ' : '展開する'}
            data-testid={`nas-tree-toggle-${row.node_id}`}
            className="h-8 w-6 flex items-center justify-center text-zinc-400 hover:text-zinc-700">
            {loading[row.node_id] ? <CaretRight size={12} className="animate-pulse" />
              : expanded[row.node_id] ? <CaretDown size={12} /> : <CaretRight size={12} />}
          </button>
          <button type="button" onClick={() => onSelect(row)}
            data-testid={`nas-tree-node-${row.node_id}`}
            className={`flex-1 flex items-center gap-1.5 px-2 min-h-[32px] rounded-md text-sm text-left transition-colors ${
              selectedId === row.node_id ? 'bg-teal-50 text-teal-900 font-semibold' : 'text-zinc-700 hover:bg-zinc-100'}`}>
            <Folder size={14} className="text-zinc-400 shrink-0" />
            <span className="truncate">{row.name}</span>
          </button>
        </div>
      ))}
    </aside>
  );
}
